import React from 'react';
import { QRCodeSVG } from 'qrcode.react';

/**
 * QRCodeDisplay component for luggage tags
 * Displays QR code with black header and footer bars
 * 
 * @param {Object} props
 * @param {string} props.name - Name to encode in QR code
 * @param {string} props.email - Email to encode in QR code
 * @param {string} props.phone - Phone to encode in QR code
 * @param {string} props.profileLink - Profile link (optional, Phase 2)
 * @param {string} props.headerText - Text for header (optional)
 * @param {string} props.footerText - Text for footer (optional)
 * @param {string} props.format - QR code format: 'simple' or 'vcard' (default: 'simple')
 * @param {number} props.size - Size of QR code (default: 200)
 */
export default function QRCodeDisplay({ 
  name, 
  email, 
  phone, 
  profileLink = '', 
  headerText = "I'm Lost",
  footerText = 'Scan Me',
  format = 'simple',
  size = 200,
  displayName = '',
  showDisplayInfo = true,
  includeWhatsApp = true
}) {
  // Helper function to escape vCard values (escape ; , \ and newlines)
  // Note: Colons (:) do NOT need to be escaped in vCard values
  const escapeVCardValue = (value) => {
    if (!value) return '';
    return String(value)
      .replace(/\\/g, '\\\\')  // Escape backslashes first
      .replace(/;/g, '\\;')    // Escape semicolons
      .replace(/,/g, '\\,')    // Escape commas
      .replace(/\n/g, '\\n')   // Escape actual newlines in values
      .replace(/\r/g, '');     // Remove carriage returns
  };

  // Helper function to convert phone number to WhatsApp format
  // WhatsApp links use: https://wa.me/PHONENUMBER (international format, no +, spaces, or dashes)
  const formatPhoneForWhatsApp = (phoneNumber) => {
    if (!phoneNumber) return '';
    // Remove all non-digit characters except leading +
    let cleaned = phoneNumber.trim();
    // Remove +, spaces, dashes, parentheses, dots
    cleaned = cleaned.replace(/[\s\-\(\)\.\+]/g, '');
    // If it doesn't start with a digit, try to extract digits only
    if (!/^\d/.test(cleaned)) {
      cleaned = cleaned.replace(/\D/g, '');
    }
    return cleaned;
  };

  // Generate QR code content based on format
  let qrContent;
  
  if (format === 'vcard') {
    // vCard format - will be recognized by most QR scanners and display with icons
    // N field: Lastname;Firstname;Additional;Prefix;Suffix
    // Split name into first and last name for proper N field
    const nameParts = name.trim().split(/\s+/);
    const lastName = nameParts.length > 1 ? nameParts.slice(-1)[0] : '';
    const firstName = nameParts.length > 1 ? nameParts.slice(0, -1).join(' ') : nameParts[0] || '';
    
    // Escape all values for vCard format
    const escapedName = escapeVCardValue(name);
    const escapedLastName = escapeVCardValue(lastName);
    const escapedFirstName = escapeVCardValue(firstName);
    const escapedPhone = escapeVCardValue(phone);
    const escapedEmail = escapeVCardValue(email);
    const escapedUrl = escapeVCardValue(profileLink);
    
    // Generate WhatsApp URL from phone number
    const whatsappPhone = formatPhoneForWhatsApp(phone);
    const whatsappUrl = whatsappPhone ? `https://wa.me/${whatsappPhone}` : '';
    const escapedWhatsAppUrl = escapeVCardValue(whatsappUrl);
    
    // Build vCard with proper line breaks (\n for QR codes) and TYPE parameters
    // Order: FN before N (matching working example)
    qrContent = `BEGIN:VCARD\nVERSION:3.0\nFN:${escapedName}\nN:${escapedLastName};${escapedFirstName};;;\nTEL;TYPE=CELL:${escapedPhone}\nEMAIL;TYPE=WORK:${escapedEmail}`;
    
    // Add WhatsApp URL if enabled and phone number is valid
    if (includeWhatsApp && escapedWhatsAppUrl) {
      qrContent += `\nURL;TYPE=WHATSAPP:${escapedWhatsAppUrl}`;
    }
    
    // Add profile link if provided
    if (escapedUrl) {
      qrContent += `\nURL:${escapedUrl}`;
    }
    
    // Add powered by information
    qrContent += `\nNOTE:powered by: https://trip147.com`;
    
    qrContent += '\nEND:VCARD';
  } else {
    // Simple format - minimal text (no labels)
    qrContent = profileLink 
      ? `${name}\n${email}\n${phone}\n${profileLink}\npowered by: https://trip147.com`
      : `${name}\n${email}\n${phone}\npowered by: https://trip147.com`;
  }

  const formatLabel = format === 'vcard' ? 'vCard' : 'Simple';

  return (
    <div className="qr-code-display" style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center',
      backgroundColor: 'white',
      borderRadius: '4px'
    }}>
      {/* Display Name and Format Type (if showDisplayInfo is true) */}
      {showDisplayInfo && (displayName || format) && (
        <div style={{
          width: `${size}px`,
          textAlign: 'center',
          padding: '4px 8px',
          fontSize: '11px',
          color: '#666',
          marginBottom: '4px',
          fontWeight: '500'
        }}>
          {displayName && <div style={{ marginBottom: '2px' }}>{displayName}</div>}
          {format && <div style={{ fontSize: '10px', color: '#999' }}>{formatLabel}</div>}
        </div>
      )}
      
      {/* Header - black background, white text, centered - same width as QR code */}
      <div style={{
        backgroundColor: '#000',
        color: '#fff',
        width: `${size}px`,
        textAlign: 'center',
        padding: '8px',
        fontSize: '12px',
        fontWeight: '500',
        marginBottom: '4px'
      }}>
        {headerText}
      </div>

      {/* QR Code without logo for better readability */}
      <QRCodeSVG
        value={qrContent}
        size={size}
        level="H"
        includeMargin={false}
        style={{ marginBottom: '4px', display: 'block' }}
      />

      {/* Footer - black background, white text, centered - same width as QR code */}
      <div style={{
        backgroundColor: '#000',
        color: '#fff',
        width: `${size}px`,
        textAlign: 'center',
        padding: '8px',
        fontSize: '12px',
        fontWeight: '500'
      }}>
        {footerText}
      </div>
    </div>
  );
}
